import { createInstance } from '@amplitude/analytics-browser';
import type { EventOptions } from '@amplitude/analytics-types';

import { environment } from '@doger/chat/shared/config';

export class AmplitudeService<
  TEvent extends {
    event: string;
    properties?: Record<string, unknown>;
  },
> {
  private readonly instance = createInstance();

  public init(consent: boolean): void {
    if (environment.stage !== 'PROD') {
      return;
    }

    this.instance.init(environment.amplitude.apiKey, {
      appVersion: environment.amplitude.release ?? 'DEV',

      identityStorage: consent ? 'localStorage' : 'cookie',

      autocapture: {
        pageViews: false,
      },

      trackingOptions: {
        language: false,
      },

      serverZone: 'EU',
    });

    window.addEventListener('pagehide', () => {
      this.instance.setTransport('beacon');
      this.instance.flush();
    });
  }

  public track(event: TEvent & { options?: EventOptions }): void {
    if (environment.stage !== 'PROD') {
      return;
    }

    this.instance.track(event.event, event.properties, event.options);
  }

  public identify(userId: string | null): void {
    if (environment.stage !== 'PROD') {
      return;
    }

    if (userId) {
      this.instance.setUserId(userId);
    } else {
      this.instance.setUserId(undefined);
    }
  }

  public consent(consent: boolean): void {
    if (environment.stage !== 'PROD') {
      return;
    }

    try {
      // https://amplitude.com/docs/sdks/analytics/browser/cookies-and-consent-management#deferred-initialization-approach
      this.init(consent);
    } catch (error) {
      console.error('Failed to consent to Amplitude:', error);
    }
  }
}
